import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Success Stories', path: '/success-stories' },
    { name: 'Operations Flow', path: '/operations-flow' },
    { name: 'About', path: '/about' },
    { name: 'Dashboard', path: 'https://dash.deltaops.health', isExternal: true },
    { name: 'Contact', path: '/contact' },
  ];

  const handleNavClick = (targetPath) => {
    if (location.pathname === targetPath) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-slate-950/95 backdrop-blur-xl border-b border-cyan-900/40 shadow-2xl'
          : 'bg-gradient-to-b from-slate-950/95 to-transparent'
      }`}
      data-testid="main-navigation"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo" onClick={() => handleNavClick("/")}>
            <span className="text-2xl font-bold transition-colors text-white">
              <img
                src="./media/logos/logo-dark.png"
                alt="DeltaOpsAI Logo"
                className="h-8 w-8 mr-2 inline-block"
              />
              DeltaOps<span className="text-cyan-300">AI</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              link.isExternal ? (
                <a
                  key={link.path}
                  href={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  className={`font-medium tracking-wide transition-colors ${
                    isScrolled
                      ? 'text-slate-200 hover:text-cyan-300'
                      : 'text-slate-100 hover:text-cyan-200'
                  }`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  onClick={() => handleNavClick(link.path)}
                  className={`font-medium tracking-wide transition-colors ${
                    isScrolled
                      ? 'text-slate-200 hover:text-cyan-300'
                      : 'text-slate-100 hover:text-cyan-200'
                  } ${location.pathname === link.path ? 'text-cyan-300' : ''}`}
                >
                  {link.name}
                </Link>
              )
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              onClick={() => handleNavClick('/contact')}
              className="px-6 py-2 rounded-lg font-semibold transition-all bg-gradient-to-r from-cyan-500 via-sky-500 to-blue-600 text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              Request Demo
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isScrolled
                ? 'text-slate-100 hover:bg-white/5'
                : 'text-white hover:bg-white/10'
            }`}
            data-testid="mobile-menu-button"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden bg-slate-950/95 border-t border-cyan-900/40 py-4"
            data-testid="mobile-menu"
          >
            {navLinks.map((link) => (
              link.isExternal ? (
                <a
                  key={link.path}
                  href={link.path}
                  className="block px-4 py-3 font-medium transition-colors rounded-lg mx-2 text-slate-200 hover:bg-white/5"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 font-medium transition-colors rounded-lg mx-2 ${
                    location.pathname === link.path
                      ? 'text-cyan-300 bg-white/5'
                      : 'text-slate-200 hover:bg-white/5'
                  }`}
                  onClick={() => handleNavClick(link.path)}
                >
                  {link.name}
                </Link>
              )
            ))}
            <Link
              to="/contact"
              onClick={() => handleNavClick('/contact')}
              className="block mx-4 mt-4 px-6 py-3 text-center bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold"
            >
              Request Demo
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
